/**
 * industryData — E13 content map for the 9 dedicated commercial industry
 * pages. Names match the platform-wide service line registry exactly.
 */
export const INDUSTRIES = {
  office: {
    necs: 'NECS Office Pro', label: 'Corporate Offices / Office Buildings', icon: '🏬',
    hero: 'PROFESSIONAL FACILITY CARE FOR CORPORATE OFFICES',
    problems: ['Inconsistent janitorial quality across floors', 'Restroom complaints from tenants', 'After-hours access coordination', 'Waste and recycling overflow'],
    faqs: [
      { q: 'Can NECS service after business hours?', a: 'Yes — programs are built around your operating hours, including nightly and weekend service windows.' },
      { q: 'Do you handle multi-tenant buildings?', a: 'Yes — common areas, tenant suites and shared amenities can all be scoped into one program.' },
    ],
  },
  retail: {
    necs: 'NECS Retail Clean', label: 'Retail Stores / Retail Centers', icon: '🛍️',
    hero: 'STOREFRONT-READY CLEANLINESS FOR RETAIL CENTERS',
    problems: ['First-impression storefronts and entries', 'Parking lot litter and cart areas', 'High-traffic restrooms', 'Overnight service needs'],
    faqs: [
      { q: 'Can you service before opening hours?', a: 'Yes — overnight and early-morning windows keep storefronts ready before customers arrive.' },
      { q: 'Do you cover common areas in multi-store centers?', a: 'Yes — walkways, parking areas and shared spaces are scoped into center-wide programs.' },
    ],
  },
  gas_station: {
    necs: 'NECS Station Care', label: 'Gas Stations / Convenience Stores', icon: '⛽',
    hero: 'ALWAYS-ON FACILITY CARE FOR GAS STATIONS AND C-STORES',
    problems: ['Fuel-island appearance and litter', '24/7 restroom upkeep', 'Dumpster and waste-area odor', 'Pump-area pressure washing'],
    faqs: [
      { q: 'Do you clean fuel spills?', a: 'NECS does not provide hazardous spill remediation. We keep fuel islands, walkways and waste areas clean within ordinary service scope.' },
      { q: 'Can you service 24/7 locations?', a: 'Yes — service windows are built around your busiest and quietest hours.' },
    ],
  },
  school: {
    necs: 'NECS Campus Clean', label: 'Schools / Educational Facilities', icon: '🏫',
    hero: 'CLASSROOM-TO-CAMPUS CLEANLINESS FOR EDUCATIONAL FACILITIES',
    problems: ['Daily classroom turnover', 'Cafeteria and gym sanitation', 'Summer deep-clean cycles', 'Safety-first product requirements'],
    faqs: [
      { q: 'Are your products safe around students?', a: 'We prioritize environmentally responsible products where performance, safety and operational requirements allow.' },
      { q: 'Do you handle summer deep cleans?', a: 'Yes — floor restoration, deep cleaning and project work are scheduled around academic calendars.' },
    ],
  },
  medical: {
    necs: 'NECS MedSafe', label: 'Medical Offices / Clinics', icon: '🏥',
    hero: 'DISCIPLINED FACILITY CARE FOR MEDICAL OFFICES AND CLINICS',
    problems: ['Patient-area sanitation standards', 'Waiting room turnover', 'Exam room protocols', 'Non-regulated waste handling'],
    faqs: [
      { q: 'Do you handle medical waste?', a: 'NECS does not handle regulated biohazardous or medical waste. We service ordinary waste streams and facility cleaning only.' },
      { q: 'Can programs follow our clinic protocols?', a: 'Yes — service checklists are built to your facility protocols and reviewed with your team.' },
    ],
  },
  warehouse: {
    necs: 'NECS Industrial', label: 'Warehouses / Industrial Facilities', icon: '🏭',
    hero: 'SCALED FACILITY CARE FOR WAREHOUSES AND INDUSTRIAL SITES',
    problems: ['Large-footprint floor zones', 'Break room and locker sanitation', 'Loading dock debris', 'Office pod upkeep inside industrial space'],
    faqs: [
      { q: 'Can you handle very large square footage?', a: 'Yes — programs scale by zone with equipment suited to industrial floor areas.' },
      { q: 'Do you work around shift schedules?', a: 'Yes — service windows are coordinated with your production and logistics shifts.' },
    ],
  },
  restaurant: {
    necs: 'NECS DineClean', label: 'Restaurants / Food-Service Facilities', icon: '🍽️',
    hero: 'FRONT-OF-HOUSE TO BACK-DOOR CARE FOR FOOD-SERVICE FACILITIES',
    problems: ['Dining area turnover', 'Grease and odor control at waste areas', 'Restroom standards under heavy traffic', 'Patio and exterior appearance'],
    faqs: [
      { q: 'Do you pump grease traps?', a: 'NECS does not provide grease-trap pumping unless properly licensed. Waste-area cleaning and odor control are within scope.' },
      { q: 'Can you service between shifts?', a: 'Yes — programs fit between lunch, dinner and overnight windows.' },
    ],
  },
  mall: {
    necs: 'NECS MallCare', label: 'Shopping Centers / Malls', icon: '🏦',
    hero: 'CENTER-WIDE FACILITY CARE FOR SHOPPING CENTERS AND MALLS',
    problems: ['Food court volume', 'Common-area restrooms', 'Entrance and corridor appearance', 'Parking structure cleanliness'],
    faqs: [
      { q: 'Do you coordinate with anchor tenants?', a: 'Yes — center-wide programs are scoped with management and can layer tenant-specific service.' },
      { q: 'Can you provide day porters?', a: 'Yes — day porter coverage is a core MallCare specialty for high-traffic hours.' },
    ],
  },
  church: {
    necs: 'NECS ChurchCare', label: 'Churches / Worship Facilities', icon: '⛪',
    hero: 'RESPECTFUL FACILITY CARE FOR CHURCHES AND WORSHIP FACILITIES',
    problems: ['Weekend service turnover', 'Fellowship hall and kitchen upkeep', 'Classroom and nursery sanitation', 'Event-driven scheduling'],
    faqs: [
      { q: 'Can you clean between weekend services?', a: 'Yes — rapid-turnover programs keep sanctuaries and common areas ready between gatherings.' },
      { q: 'Do you handle event cleanup?', a: 'Yes — event-based deep cleans and bulk removal can be added to any ChurchCare program.' },
    ],
  },
  // Iter-149 · Section 14 — BankCare (Strategic Enterprise #10).
  bank: {
    necs: 'NECS BankCare', label: 'Banking & Financial Facilities', icon: '🏦',
    hero: 'SECURE, PROFESSIONAL FACILITY CARE FOR FINANCIAL INSTITUTIONS',
    tier: 'strategic_enterprise',
    priority_order: ['Security & professionalism', 'Customer experience', 'Sanitation', 'Floors', 'Appearance'],
    problems: ['Customer-lobby appearance', 'High-touch teller counters', 'Restroom and break-room upkeep', 'Secure and restricted-area protocols'],
    faqs: [
      { q: 'Does NECS access vaults or cash rooms?', a: 'No — secure and restricted-access areas are serviced only according to the client\'s authorized security procedures. NECS does not automatically access vaults, cash rooms, safe-deposit areas, or restricted IT rooms.' },
      { q: 'Can NECS service outside banking hours?', a: 'Yes — nightly programs and daytime porter coverage are both available.' },
    ],
  },
  // Iter-149 · Section 15 — StadiumCare (Strategic Enterprise #11).
  stadium: {
    necs: 'NECS StadiumCare', label: 'Stadiums & Arenas', icon: '🏟️',
    hero: 'HIGH-PERFORMANCE FACILITY CARE FOR HIGH-TRAFFIC VENUES',
    tier: 'strategic_enterprise',
    priority_order: ['Event readiness', 'Restrooms', 'Waste', 'High-traffic areas', 'Turnaround speed', 'Appearance'],
    problems: ['Rapid pre- and post-event turnaround', 'High-volume restroom service', 'Waste volume during events', 'Concourse and seating restoration'],
    faqs: [
      { q: 'How is StadiumCare priced?', a: 'Stadium programs are priced using facility base, square footage, attendance, event frequency, event duration, turnaround windows, restroom count, seating, concessions, premium areas, waste volume, labor, and specialty services — not by square footage alone.' },
      { q: 'Can NECS provide event-day teams?', a: 'Yes — Executive Care includes an account manager, site supervisor, cleaning, restroom, day-porter, waste/recycling and floor specialist teams.' },
    ],
  },
  // Iter-149 · Section 16 — AirportCare (Strategic Enterprise #12).
  airport: {
    necs: 'NECS AirportCare', label: 'Airports & Aviation Facilities', icon: '✈️',
    hero: 'PRECISION FACILITY CARE FOR HIGH-TRAFFIC AVIATION ENVIRONMENTS',
    tier: 'strategic_enterprise',
    priority_order: ['Passenger experience', 'Sanitation', 'Traffic management', 'Continuous operations', 'Waste', 'Security compliance'],
    problems: ['Continuous 24/7 operations', 'High passenger throughput', 'Restroom volume and turnover', 'Restricted and secure area protocols'],
    faqs: [
      { q: 'Does NECS access secure aviation areas?', a: 'Restricted and secure areas require applicable airport authorization, credentials, and client-approved operational procedures. NECS never implies unrestricted access to secure aviation areas.' },
      { q: 'Can AirportCare run 24/7?', a: 'Yes — continuous and day-porter options are core to AirportCare Professional and Executive programs.' },
    ],
  },
};

export const SPECIALTY_SERVICES = [
  { name: 'Floor Care', desc: 'Routine floor cleaning programs for every surface type.' },
  { name: 'Floor Polishing', desc: 'Professional floor-care programs designed to restore appearance, protect surfaces and maintain a consistently professional environment.' },
  { name: 'Floor Restoration', desc: 'Professional floor-care programs designed to restore appearance, protect surfaces and maintain a consistently professional environment.' },
  { name: 'Window Cleaning', desc: 'Interior and exterior window programs, scheduled to your facility.' },
  { name: 'Pressure Washing', desc: 'Exterior surfaces, entries, pads and walkways.' },
  { name: 'Property Appearance Patrol', desc: 'Scheduled exterior inspections and removal of visible ordinary litter and debris from designated property areas.' },
  { name: 'Bulk Removal', desc: 'Scheduled bulk item pickup and cleanout support.' },
  { name: 'Dumpster & Trash Room Care', desc: 'Treatment, deodorizing and waste-area maintenance.' },
  { name: 'Deep Cleaning', desc: 'Project-based deep cleans for any facility zone.' },
  { name: 'Day Porter', desc: 'On-site daytime coverage for high-traffic facilities.' },
  { name: 'Amenity Cleaning', desc: 'Shared amenity and common-area cleaning programs.' },
  { name: 'Recurring Sanitation', desc: 'Scheduled sanitation rounds for high-touch areas.' },
];
